import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileSpreadsheet, 
  Home, 
  Building2, 
  Calendar, 
  CheckCircle2, 
  AlertTriangle,
  Phone,
  Mail,
  Clock,
  Award,
  Target,
  TrendingUp,
  Thermometer,
  Zap,
  Euro,
  FileCheck,
  Wind,
  FileText,
  Shield
} from 'lucide-react';

const AuditEnergetique = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-32 pb-16 px-4 bg-gradient-to-br from-white to-gray-100"
    >
      <div className="container mx-auto">
        {/* Hero Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div variants={itemVariants} className="flex justify-center mb-6">
            <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue p-4 rounded-full">
              <FileSpreadsheet className="h-12 w-12 text-white" />
            </div>
          </motion.div>
          <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Audit Énergétique Certifié
          </motion.h1>
          <motion.p variants={itemVariants} className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Analyse approfondie obligatoire pour la vente des biens immobiliers classés E, F ou G (passoires thermiques).
            Notre audit propose des scenarios de travaux detailles pour ameliorer la performance energetique de votre logement.
          </motion.p>
        </motion.div>

        {/* Section À quoi sert l'audit énergétique */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <motion.div variants={itemVariants} className="flex items-center mb-8">
              <Target className="h-8 w-8 text-dk-blue mr-4" />
              <h2 className="text-3xl font-bold text-gray-900">À quoi sert l'audit énergétique ?</h2>
            </motion.div>
            
            <motion.div variants={itemVariants} className="prose prose-lg max-w-none mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                L'audit énergétique est un diagnostic immobilier qui consiste à réaliser un état des lieux 
                de la performance énergétique et environnementale des bâtiments.
              </p>
            </motion.div>

            <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div variants={itemVariants} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <TrendingUp className="h-6 w-6 text-dk-blue mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">Sensibilisation écologique</h3>
                </div>
                <p className="text-gray-700">
                  Il vise à sensibiliser le futur acquéreur aux questions écologiques et énergétiques 
                  du bâtiment qu'il projette d'acheter.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Thermometer className="h-6 w-6 text-dk-blue mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">Propositions d'amélioration</h3>
                </div>
                <p className="text-gray-700">
                  Il oriente en proposant des travaux pour améliorer le confort thermique, 
                  la qualité de l'air et parvenir à une rénovation performante.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Section Bâtiments concernés */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 md:p-12">
            <motion.div variants={itemVariants} className="flex items-center mb-8">
              <Building2 className="h-8 w-8 text-white mr-4" />
              <h2 className="text-3xl font-bold">Quels bâtiments sont concernés ?</h2>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-8">
              <p className="text-lg leading-relaxed mb-6">
                L'obligation de realiser un audit energetique concerne les logements mis en vente
                classes E, F ou G au DPE. Les classes A, B, C et D ne sont pas concernees.
              </p>
            </motion.div>

            <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <motion.div variants={itemVariants} className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-300 mr-3" />
                  <h3 className="text-xl font-semibold">Classes F et G</h3>
                </div>
                <p className="text-white/90">
                  Obligatoire depuis le 1er avril 2023 pour les logements les plus energivores
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <Calendar className="h-6 w-6 text-yellow-300 mr-3" />
                  <h3 className="text-xl font-semibold">Classe E</h3>
                </div>
                <p className="text-white/90">
                  Obligatoire depuis le 1er janvier 2025 pour les logements de classe E
                </p>
              </motion.div>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
              <div className="flex items-start space-x-4">
                <Home className="h-6 w-6 text-white flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold mb-3">Types de bâtiments concernés</h3>
                  <ul className="space-y-2 text-white/90">
                    <li className="flex items-center">
                      <CheckCircle2 className="h-4 w-4 mr-2 text-green-300" />
                      Maisons individuelles
                    </li>
                    <li className="flex items-center">
                      <CheckCircle2 className="h-4 w-4 mr-2 text-green-300" />
                      Immeubles dont vous êtes l'unique propriétaire
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Section Missions de l'auditeur */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <motion.div variants={itemVariants} className="flex items-center mb-8">
              <Award className="h-8 w-8 text-dk-blue mr-4" />
              <h2 className="text-3xl font-bold text-gray-900">Missions de l'auditeur énergétique</h2>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 mb-8">
              <div className="flex items-center mb-4">
                <Clock className="h-6 w-6 text-dk-blue mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Visite obligatoire</h3>
              </div>
              <p className="text-gray-700">
                L'auditeur doit effectuer au moins 1 visite du bâtiment en votre présence 
                ou celle d'une tierce personne que vous avez mandatée.
              </p>
            </motion.div>

            <motion.div variants={itemVariants}>
              <h3 className="text-xl font-semibold mb-6 text-gray-900">Documents à fournir à l'auditeur :</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <FileSpreadsheet className="h-6 w-6 text-dk-blue mr-3" />
                    <h4 className="font-semibold text-gray-900">DPE</h4>
                  </div>
                  <p className="text-gray-700 text-sm">Récapitulatif de votre Diagnostic de Performance Énergétique</p>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Euro className="h-6 w-6 text-dk-blue mr-3" />
                    <h4 className="font-semibold text-gray-900">Factures</h4>
                  </div>
                  <p className="text-gray-700 text-sm">Vos factures de travaux déjà réalisés</p>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <FileCheck className="h-6 w-6 text-dk-blue mr-3" />
                    <h4 className="font-semibold text-gray-900">Diagnostics</h4>
                  </div>
                  <p className="text-gray-700 text-sm">Diagnostics techniques (électricité, gaz, etc.)</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Section Contenu de l'audit */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <motion.div variants={itemVariants} className="flex items-center mb-8">
              <FileCheck className="h-8 w-8 text-dk-blue mr-4" />
              <h2 className="text-3xl font-bold text-gray-900">Que contient l'audit énergétique ?</h2>
            </motion.div>

            <motion.div variants={itemVariants} className="prose prose-lg max-w-none mb-8">
              <p className="text-gray-700 leading-relaxed">
                L'audit énergétique est un document complet qui comprend plusieurs éléments essentiels 
                pour vous guider dans votre projet de rénovation.
              </p>
            </motion.div>

            <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <motion.div variants={itemVariants} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Thermometer className="h-6 w-6 text-dk-blue mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Performance avant travaux</h3>
                </div>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• Estimation selon méthode DPE</li>
                  <li>• Schéma des déperditions thermiques</li>
                  <li>• Dispositifs de pilotage existants</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Target className="h-6 w-6 text-dk-blue mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Propositions de travaux</h3>
                </div>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• 2 propositions minimum</li>
                  <li>• Parcours par étapes cohérentes</li>
                  <li>• Compatible avec le bâti existant</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Euro className="h-6 w-6 text-dk-blue mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Coûts et aides</h3>
                </div>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• Estimation des coûts de travaux</li>
                  <li>• Aides financières mobilisables</li>
                  <li>• Économies d'énergie attendues</li>
                </ul>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Section Propositions de travaux détaillées */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 md:p-12">
            <motion.div variants={itemVariants} className="flex items-center mb-8">
              <TrendingUp className="h-8 w-8 text-white mr-4" />
              <h2 className="text-3xl font-bold">Les propositions de travaux</h2>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-8">
              <p className="text-lg leading-relaxed mb-6">
                L'audit propose 2 scénarios de rénovation pour atteindre une performance énergétique optimale :
              </p>
            </motion.div>

            <motion.div variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <motion.div variants={itemVariants} className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-white/20 p-3 rounded-full mr-4">
                    <span className="text-2xl font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-semibold">Rénovation par étapes</h3>
                </div>
                <div className="space-y-3">
                  <p className="text-white/90 mb-4">Parcours progressif permettant d'étaler les investissements :</p>
                  <div className="bg-white/10 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">1ère étape obligatoire :</h4>
                    <ul className="text-sm text-white/90 space-y-1">
                      <li>• Gain d'au moins 2 classes énergétiques</li>
                      <li>• Traitement de 2 postes d'isolation minimum</li>
                    </ul>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Objectif final :</h4>
                    <p className="text-sm text-white/90">Atteindre au minimum la classe B</p>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-white/20 p-3 rounded-full mr-4">
                    <span className="text-2xl font-bold">2</span>
                  </div>
                  <h3 className="text-xl font-semibold">Rénovation globale</h3>
                </div>
                <div className="space-y-3">
                  <p className="text-white/90 mb-4">Rénovation complète en une seule fois :</p>
                  <div className="bg-white/10 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Avantages :</h4>
                    <ul className="text-sm text-white/90 space-y-1">
                      <li>• Performance optimale immédiate</li>
                      <li>• Cohérence technique maximale</li>
                      <li>• Économies d'échelle possibles</li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
              <h3 className="text-xl font-semibold mb-4">Les 6 postes de travaux étudiés</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  'Isolation des murs',
                  'Isolation des planchers bas',
                  'Isolation de la toiture',
                  'Remplacement des menuiseries',
                  'Système de ventilation',
                  'Chauffage et eau chaude'
                ].map((poste, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-300" />
                    <span className="text-sm text-white/90">{poste}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Section Performance après travaux */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <motion.div variants={itemVariants} className="flex items-center mb-8">
              <Zap className="h-8 w-8 text-dk-blue mr-4" />
              <h2 className="text-3xl font-bold text-gray-900">Performance énergétique après travaux</h2>
            </motion.div>

            <motion.div variants={itemVariants} className="prose prose-lg max-w-none mb-8">
              <p className="text-gray-700 leading-relaxed">
                L'audit détaille précisément les gains de performance pour chaque étape de travaux, 
                vous permettant de mesurer l'impact de votre investissement.
              </p>
            </motion.div>

            <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div variants={itemVariants} className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Thermometer className="h-5 w-5 text-dk-blue mr-2" />
                    Consommations énergétiques
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• Consommation annuelle par usage (kWhEP/m²/an)</li>
                    <li>• Consommation totale avec/sans photovoltaïque</li>
                    <li>• Comparaison avant/après travaux</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Target className="h-5 w-5 text-dk-blue mr-2" />
                    Classements énergétiques
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• Nouveau classement DPE (A à G)</li>
                    <li>• Classement gaz à effet de serre</li>
                    <li>• Évolution par rapport à l'état initial</li>
                  </ul>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="space-y-6">
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Euro className="h-5 w-5 text-dk-blue mr-2" />
                    Économies financières
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• Estimation des économies d'énergie</li>
                    <li>• Réduction des frais annuels</li>
                    <li>• Fourchette d'économie de coûts</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Wind className="h-5 w-5 text-dk-blue mr-2" />
                    Impact environnemental
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• Réduction des émissions CO2</li>
                    <li>• Amélioration de la qualité de l'air</li>
                    <li>• Contribution à la transition énergétique</li>
                  </ul>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Section Forme de l'audit */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl shadow-lg p-8 md:p-12">
            <motion.div variants={itemVariants} className="flex items-center mb-8">
              <FileText className="h-8 w-8 text-dk-blue mr-4" />
              <h2 className="text-3xl font-bold text-gray-900">Forme et validité de l'audit</h2>
            </motion.div>

            <motion.div variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <motion.div variants={itemVariants} className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-center mb-4">
                  <FileSpreadsheet className="h-6 w-6 text-dk-blue mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">Rapport de synthèse (PDF)</h3>
                </div>
                <ul className="text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-500 mt-1" />
                    <span>État des lieux détaillé du bâtiment</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-500 mt-1" />
                    <span>Synthèse complète de l'audit</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-500 mt-1" />
                    <span>Annexe technique explicative</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-500 mt-1" />
                    <span>Contacts pour accompagnement</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-dk-blue font-medium">
                    📅 Délai de remise : 1 mois maximum après la visite
                  </p>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-center mb-4">
                  <FileCheck className="h-6 w-6 text-dk-blue mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">Récapitulatif standardisé (XML)</h3>
                </div>
                <ul className="text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-500 mt-1" />
                    <span>Données administratives du bâtiment</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-500 mt-1" />
                    <span>Caractéristiques techniques détaillées</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-500 mt-1" />
                    <span>Indicateurs de performance</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-500 mt-1" />
                    <span>Analyse économique complète</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700 font-medium">
                    ⏱️ Validité : 5 ans
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-dk-blue text-white rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Shield className="h-6 w-6 text-white mr-3" />
                <h3 className="text-xl font-semibold">Conservation et transmission</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2">Obligations de l'auditeur :</h4>
                  <ul className="text-white/90 space-y-1 text-sm">
                    <li>• Conservation des fichiers informatiques</li>
                    <li>• Mise à disposition pendant 5 ans</li>
                    <li>• Transmission aux propriétaires successifs</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Formats de remise :</h4>
                  <ul className="text-white/90 space-y-1 text-sm">
                    <li>• Version papier</li>
                    <li>• Version informatique (PDF + XML)</li>
                    <li>• Accessibilité garantie</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Call to Action */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center"
        >
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Besoin d'un audit énergétique certifié ?</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Notre diagnostiqueur certifié vous accompagne dans la réalisation de votre audit énergétique 
              selon les dernières réglementations en vigueur.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact?subject=audit&message=Bonjour, je souhaiterais obtenir des informations sur vos audits énergétiques et convenir d'un rendez-vous. Merci de me recontacter."
                className="btn bg-white text-dk-blue hover:bg-gray-100 text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Mail className="h-5 w-5 mr-2" />
                Demander un audit
              </Link>
              <a 
                href="tel:0789307500"
                className="btn bg-white/10 hover:bg-white/20 text-white border-2 border-white text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="h-5 w-5 mr-2" />
                07 89 30 75 00
              </a>
            </div>
          </motion.div>
        </motion.section>
      </div>
    </motion.div>
  );
};

export default AuditEnergetique;